// ABOUTME: REST hook for the CAD artifact download (GET options + POST /api/configurator/cad-download).
// ABOUTME: Turns the POST response into a browser download, narrated by the shared toasts.

import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useCallback, useEffect, useState } from 'react';
import { IntlShape, useIntl } from 'react-intl';

/** One format the tenant offers, as the options endpoint reports it. */
export interface CadDownloadFormat {
    code: string;
    label: string;
    /**
     * Whether the portal will generate it today, as its own qualifier list reports. The entry is
     * still listed when false — so a tenant sees that what they configured is unavailable rather
     * than wondering where it went — but it cannot be clicked.
     */
    available: boolean;
    /** `'2d'` or `'3d'` as the portal classifies it; the menu picks its icon from this. */
    cad?: string;
}

export interface CadDownloadOptions {
    formats: CadDownloadFormat[];
}

type CadDownloadOptionsResponse = { available: false } | ({ available: true } & CadDownloadOptions);

/**
 * Which menu entry is currently running: the format code, so one format generating does not block
 * the rest of the menu.
 */
export type CadDownloadEntry = string;

interface DownloadRequest {
    configurationJson: string;
    format: string;
    /** Lets the server resolve quoteInfo itself, exactly like /apply-assignments does. */
    offerId?: string;
    lineItemId?: string;
}

// `attachment; filename="g3.pdf"` -> `g3.pdf`. The server always sends one; the fallback only
// covers a proxy that stripped the header.
const fileNameFromDisposition = (disposition: string | null, fallback: string): string => {
    const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    return match?.[1] ? decodeURIComponent(match[1]) : fallback;
};

/** Mirrors server/src/productCatalog/services/cadService/types.ts's CadDownloadUnavailableReason. */
type CadDownloadUnavailableReason =
    | 'CAD_NOT_CONFIGURED'
    | 'NO_CAD_ATTRIBUTES'
    | 'VARSET_TOO_LONG'
    | 'INVALID_CAD_VALUE'
    | 'CONFIGURATION_INCOMPLETE'
    | 'DOWNLOAD_NOT_CONFIGURED'
    | 'FORMAT_NOT_SUPPORTED';

// One seller-facing message per reason, mirroring the CODE_MEANINGS pattern in PricingErrorPanel:
// the server carries a reason code, not copy, so the translation lives here.
const REASON_MESSAGES: Record<
    CadDownloadUnavailableReason,
    { id: string; defaultMessage: string }
> = {
    CAD_NOT_CONFIGURED: {
        id: 'cpq.cad-download.reason.CAD_NOT_CONFIGURED',
        defaultMessage: 'This configuration has no CAD project to generate a document from.',
    },
    NO_CAD_ATTRIBUTES: {
        id: 'cpq.cad-download.reason.NO_CAD_ATTRIBUTES',
        defaultMessage: 'None of the selected options carry a CAD attribute.',
    },
    VARSET_TOO_LONG: {
        id: 'cpq.cad-download.reason.VARSET_TOO_LONG',
        defaultMessage: 'The configuration is too complex to generate a CAD document for.',
    },
    INVALID_CAD_VALUE: {
        id: 'cpq.cad-download.reason.INVALID_CAD_VALUE',
        defaultMessage: 'The configuration contains a value the CAD system cannot accept.',
    },
    CONFIGURATION_INCOMPLETE: {
        id: 'cpq.cad-download.reason.CONFIGURATION_INCOMPLETE',
        defaultMessage: 'The configuration must be completed before generating this document.',
    },
    DOWNLOAD_NOT_CONFIGURED: {
        id: 'cpq.cad-download.reason.DOWNLOAD_NOT_CONFIGURED',
        defaultMessage: 'CAD downloads are not available for this product.',
    },
    FORMAT_NOT_SUPPORTED: {
        id: 'cpq.cad-download.reason.FORMAT_NOT_SUPPORTED',
        defaultMessage: 'The requested format is not supported for this product.',
    },
};

const readError = async (
    response: Response,
    formatMessage: IntlShape['formatMessage'],
): Promise<string> => {
    const genericError = () =>
        formatMessage(
            { id: 'cpq.cad-download.error-generic', defaultMessage: 'Request failed ({status})' },
            { status: response.status },
        );
    try {
        const json = (await response.json()) as { reason?: string; error?: string };
        const reasonMessage = json.reason
            ? REASON_MESSAGES[json.reason as CadDownloadUnavailableReason]
            : undefined;
        if (reasonMessage) {
            return formatMessage(reasonMessage);
        }
        return json.error ?? genericError();
    } catch {
        return genericError();
    }
};

/**
 * Hook backing the product header's CAD download menu.
 *
 * The options are fetched once per product: the endpoint answers the whole set, so the menu renders
 * and disables its entries with no round trip per item. The artifacts themselves are fetched on
 * demand, with the configuration as it stands — the frontend never composes a CADENAS URI, it asks
 * PIM for a file.
 */
export function useCadDownload(productId: string | undefined, skip = false) {
    const [options, setOptions] = useState<CadDownloadOptions | null>(null);
    const [busyEntry, setBusyEntry] = useState<CadDownloadEntry | null>(null);
    const { formatMessage } = useIntl();

    useEffect(() => {
        if (!productId || skip) {
            return () => {};
        }
        let cancelled = false;
        // A failure leaves `options` null, which renders no CADENAS entry: the menu must never be
        // able to break the drawer it lives in.
        fetch(`/api/configurator/cad-download/options/${encodeURIComponent(productId)}`)
            .then(response => response.json() as Promise<CadDownloadOptionsResponse>)
            .then(json => {
                if (!cancelled) {
                    setOptions(json.available ? json : null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setOptions(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [productId, skip]);

    /**
     * Submits the generation request. Only the request/response exchange: narrating it (the
     * "preparing" toast, `busyEntry`) and turning the response into a download are downloadArtifact's
     * job, since it alone knows when the file actually reached the browser.
     */
    const post = useCallback(
        async (body: Record<string, unknown>): Promise<Response> => {
            const response = await fetch('/api/configurator/cad-download', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                throw new Error(await readError(response, formatMessage));
            }
            return response;
        },
        [formatMessage],
    );

    /**
     * Runs one artifact request end to end, narrating it through the shared toasts.
     *
     * The generation is polled server-side and can take up to a minute and a half, so a "preparing"
     * toast is raised immediately and then **replaced** by the outcome rather than stacked with it —
     * the same shape `useListinoDownload` uses for its own long export. `busyEntry` (and the toast it
     * gates) spans the whole flow, through the blob read and the link click: only once the browser has
     * actually started the download is the format free to be requested again.
     */
    const downloadArtifact = useCallback(
        async ({ configurationJson, format, offerId, lineItemId }: DownloadRequest) => {
            const toastKey = SnackBarUtils.info(
                formatMessage({
                    id: 'cpq.cad-download.preparing',
                    defaultMessage: 'Preparing the document…',
                }),
            );
            const fail = (reason: string) => {
                // Persisted: after a minute of waiting, a failure that fades in four seconds is a
                // failure the user never saw.
                SnackBarUtils.error(
                    formatMessage(
                        {
                            id: 'cpq.cad-download.error',
                            defaultMessage: 'The document could not be downloaded ({reason})',
                        },
                        { reason },
                    ),
                    { persist: true, replaceKey: toastKey },
                );
                return false;
            };
            setBusyEntry(format);
            try {
                const response = await post({ configurationJson, format, offerId, lineItemId });
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = fileNameFromDisposition(
                    response.headers.get('content-disposition'),
                    `${format}.zip`,
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                SnackBarUtils.success(
                    formatMessage({
                        id: 'cpq.cad-download.started',
                        defaultMessage: 'Download started',
                    }),
                    { replaceKey: toastKey },
                );
                return true;
            } catch (err) {
                return fail(
                    err instanceof Error
                        ? err.message
                        : formatMessage({
                              id: 'cpq.cad-download.error-unknown',
                              defaultMessage: 'CAD download failed',
                          }),
                );
            } finally {
                setBusyEntry(null);
            }
        },
        [post, formatMessage],
    );

    return { options, downloadArtifact, busyEntry };
}
